/**
 * EPIC 44 Phase 2 — pure helpers for the live "Praegu" (Now) operations board.
 *
 * The board answers "who is clocked in right now?" from the same booking model:
 * a check-in creates a DRAFT timesheet with one OPEN entry (checkInAt set, no
 * checkOutAt); completeWorkDay stamps the checkout and flips the sheet to
 * 'submitted'. So an active worker = a draft whose LAST entry is still open.
 *
 * No Firestore / React here — everything is deterministic and unit-tested. The
 * ticking clock stays out of these helpers: callers pass `nowMs` in so the pure
 * functions never read `Date.now()`.
 */

import type { TimesheetEntry, Worker } from '@rendimees24/shared-types';

export interface LatLng {
  latitude: number;
  longitude: number;
}

/** Default geofence radius (metres) — mirrors functions/src/jobs/lib/geo usage. */
export const DEFAULT_GEOFENCE_RADIUS_M = 300;

const EARTH_RADIUS_M = 6_371_000;

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Haversine great-circle distance in metres between two points. Pure — client
 * mirror of functions/src/jobs/lib/geo.haversineMeters (kept in-app so the
 * board has no functions dependency).
 */
export function haversineMeters(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const sinHalfLat = Math.sin(dLat / 2);
  const sinHalfLon = Math.sin(dLon / 2);
  const h =
    sinHalfLat * sinHalfLat +
    Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * sinHalfLon * sinHalfLon;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** The subset of a timesheet the open-entry check needs. */
export interface ActiveTimesheetShape {
  status: string;
  entries?: TimesheetEntry[];
}

/**
 * The worker's currently-open entry, or null. Mirrors the check-in idempotency
 * guard in services/worker.ts: only a DRAFT sheet whose LAST entry has a
 * check-in and no check-out counts as "clocked in now".
 */
export function openEntry(ts: ActiveTimesheetShape): TimesheetEntry | null {
  if (ts.status !== 'draft') return null;
  const entries = ts.entries ?? [];
  const last = entries[entries.length - 1];
  if (last && last.checkInAt && !last.checkOutAt) return last;
  return null;
}

/** True when the timesheet represents a worker who is clocked in right now. */
export function isActiveTimesheet(ts: ActiveTimesheetShape): boolean {
  return openEntry(ts) !== null;
}

/**
 * Elapsed time between `fromMs` and `nowMs` as `HH:MM:SS` (zero-padded, hours
 * un-capped). A negative or NaN span clamps to `00:00:00` so a slightly-skewed
 * client clock never renders a garbage timer.
 */
export function formatElapsed(fromMs: number, nowMs: number): string {
  const totalSec = Math.max(0, Math.floor((nowMs - fromMs) / 1000)) || 0;
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export type PinState = 'on-site' | 'off-site' | 'no-location';

export interface PinResult {
  state: PinState;
  /** Metres from the job location; null when a distance can't be computed. */
  distanceM: number | null;
}

/** A (0,0) coordinate is the check-in default when GPS was unavailable — treat as missing. */
function isRealCoord(c: LatLng | undefined | null): c is LatLng {
  return (
    !!c &&
    typeof c.latitude === 'number' &&
    typeof c.longitude === 'number' &&
    !(c.latitude === 0 && c.longitude === 0)
  );
}

/**
 * Classify the check-in location against the job site:
 *   green  ('on-site')      — within `radiusM` of the site,
 *   yellow ('off-site')     — outside the radius / only approximate,
 *   gray   ('no-location')  — no usable check-in or site coordinates.
 * Derived at read time; the persisted geofence + per-site radius is Phase 5.
 */
export function classifyPin(
  checkInLocation: LatLng | undefined | null,
  jobLocation: LatLng | undefined | null,
  radiusM: number = DEFAULT_GEOFENCE_RADIUS_M,
): PinResult {
  if (!isRealCoord(checkInLocation) || !isRealCoord(jobLocation)) {
    return { state: 'no-location', distanceM: null };
  }
  const distanceM = haversineMeters(checkInLocation, jobLocation);
  return { state: distanceM <= radiusM ? 'on-site' : 'off-site', distanceM };
}

/**
 * Pin for a check-in entry, PREFERRING the geofence result persisted at
 * check-in (EPIC 44 Phase 5: `entry.onSite` / `entry.distanceMeters`) over the
 * read-time haversine derivation. When the entry carries no persisted verdict
 * (legacy entries, or GPS/site coords were missing), fall back to `classifyPin`.
 */
export function pinForEntry(
  entry: Pick<TimesheetEntry, 'checkInLocation' | 'onSite' | 'distanceMeters'>,
  jobLocation: LatLng | undefined | null,
  radiusM: number = DEFAULT_GEOFENCE_RADIUS_M,
): PinResult {
  if (typeof entry.onSite === 'boolean') {
    return {
      state: entry.onSite ? 'on-site' : 'off-site',
      distanceM: typeof entry.distanceMeters === 'number' ? entry.distanceMeters : null,
    };
  }
  return classifyPin(entry.checkInLocation, jobLocation, radiusM);
}

/** `YYYY-MM-DD` in the local timezone for a given Date. */
export function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Whether the worker is on leave on `todayYmd` (YYYY-MM-DD): a `daysOff` entry
 * on that date, or a `holidayPeriods` span covering it (inclusive). Lightweight
 * read-time check — the proper request/approval flow is Phase 4.
 */
export function isOnLeaveToday(
  worker: Pick<Worker, 'holidayPeriods' | 'daysOff'>,
  todayYmd: string,
): boolean {
  const day = todayYmd.slice(0, 10);
  if ((worker.daysOff ?? []).some((d) => d.slice(0, 10) === day)) return true;
  return (worker.holidayPeriods ?? []).some(
    (p) => p.from.slice(0, 10) <= day && day <= p.to.slice(0, 10),
  );
}
